import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api, { extrairErro } from '../services/api';
import StatusCard from '../components/StatusCard';
import { RELACOES_GV, TIPO_TITULO, TIPO_LABEL } from '../constants/opcoes';
import { IconArrowLeft, IconAlert } from '../components/icons';

const TIPOS_ACEITOS = ['reclamacao', 'sugestao', 'elogio'];

function Manifestacao() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tipo } = useParams();
  const unidadePre = location.state?.unidadePre;

  const [categorias, setCategorias] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [form, setForm] = useState({
    categoria_id: '', descricao: '', unidade_id: unidadePre || '',
    anonima: true, nome: '', email: '', telefone: '', relacao_gv: '',
  });
  const [consentimento, setConsentimento] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const tipoValido = TIPOS_ACEITOS.includes(tipo);

  useEffect(() => {
    if (!tipoValido) return;
    const fetchData = async () => {
      try {
        const [catRes, unidRes] = await Promise.all([
          api.get('/public/categorias', { params: { tipo } }),
          api.get('/public/unidades'),
        ]);
        setCategorias(catRes.data);
        setUnidades(unidRes.data);
      } catch (err) {
        setError('Erro ao carregar dados. Tente novamente.');
      }
    };
    fetchData();
  }, [tipo, tipoValido]);

  if (!tipoValido) {
    return (
      <div className="container">
        <p>Tipo de manifestação inválido.</p>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>Voltar ao início</button>
      </div>
    );
  }

  const set = (chave, valor) => setForm((f) => ({ ...f, [chave]: valor }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.categoria_id || form.descricao.trim().length < 20) {
      setError('Preencha o assunto e descreva com pelo menos 20 caracteres');
      return;
    }
    if (!form.anonima && (!form.nome || !form.email || !form.relacao_gv)) {
      setError('Nome, e-mail e relação com o Grupo Vilarouca são obrigatórios');
      return;
    }
    if (!form.anonima && !consentimento) {
      setError('Você deve concordar com a Política de Privacidade para enviar identificado');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/public/manifestacoes', {
        tipo,
        anonima: form.anonima,
        categoria_id: form.categoria_id,
        descricao: form.descricao,
        unidade_id: form.unidade_id || undefined,
        nome: form.anonima ? undefined : form.nome,
        email: form.anonima ? undefined : form.email,
        telefone: form.anonima ? undefined : form.telefone,
        relacao_gv: form.anonima ? undefined : form.relacao_gv,
      });
      const { protocolo, pin } = res.data;

      if (!form.anonima) {
        // Registrar consentimento com a Política de Privacidade
        try {
          await api.post('/public/lgpd/consentimento', {
            protocolo,
            pin,
            tipo_consentimento: 'politica_privacidade',
            consentiu: true,
          });
        } catch (err) {
          console.error('Erro ao registrar consentimento:', err);
          // Não bloqueia o fluxo se o consentimento não for registrado
        }
      }

      setResultado(res.data);
    } catch (err) {
      setError(extrairErro(err, 'Erro ao enviar manifestação'));
    } finally {
      setLoading(false);
    }
  };

  if (resultado) {
    return (
      <div className="container">
        <button className="btn-back" onClick={() => navigate('/')}><IconArrowLeft /> Início</button>
        <StatusCard
          titulo={`${TIPO_TITULO[tipo]} recebida com sucesso`}
          protocolo={resultado.protocolo}
          pin={resultado.pin}
          tipoLabel={TIPO_LABEL[tipo]}
        />
      </div>
    );
  }

  return (
    <div className="container">
      <button className="btn-back" onClick={() => navigate('/')}><IconArrowLeft /> Início</button>
      <div className="step-heading">
        <p className="eyebrow">Ouvir · Proteger · Construir juntos</p>
        <h2>{TIPO_TITULO[tipo]}</h2>
      </div>
      {error && <div className="error-message"><IconAlert />{error}</div>}

      <form onSubmit={handleSubmit} className="form">
        <div className="form-group">
          <label>Assunto</label>
          <select value={form.categoria_id} onChange={(e) => set('categoria_id', e.target.value)} required>
            <option value="">-- Selecione --</option>
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label>Descreva com detalhes</label>
          <textarea rows={5} minLength={20} value={form.descricao} onChange={(e) => set('descricao', e.target.value)} required />
        </div>

        <div className="form-group">
          <label>Unidade (opcional)</label>
          <select value={form.unidade_id} onChange={(e) => set('unidade_id', e.target.value)}>
            <option value="">-- Não se aplica --</option>
            {unidades.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </div>

        <div className="form-group">
          <label>Como você quer registrar?</label>
          <div className="radio-group">
            <label>
              <input type="radio" name="anonima" checked={form.anonima} onChange={() => set('anonima', true)} />
              Anonimamente
            </label>
            <label>
              <input type="radio" name="anonima" checked={!form.anonima} onChange={() => set('anonima', false)} />
              Com meus dados
            </label>
          </div>
        </div>

        {!form.anonima && (
          <div className="form-section">
            <input type="text" placeholder="Nome completo" value={form.nome} onChange={(e) => set('nome', e.target.value)} required />
            <input type="email" placeholder="E-mail" value={form.email} onChange={(e) => set('email', e.target.value)} required />
            <input type="tel" placeholder="Telefone (opcional)" value={form.telefone} onChange={(e) => set('telefone', e.target.value)} />
            <select value={form.relacao_gv} onChange={(e) => set('relacao_gv', e.target.value)} required>
              <option value="">Relação com o Grupo Vilarouca</option>
              {RELACOES_GV.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <label className="checkbox-label">
              <input type="checkbox" checked={consentimento} onChange={(e) => setConsentimento(e.target.checked)} required />
              <span>
                Concordo com a <a href="/privacidade" target="_blank" rel="noopener noreferrer">Política de Privacidade</a>
              </span>
            </label>
          </div>
        )}

        <button type="submit" className="btn btn-submit" disabled={loading}>
          {loading ? 'Enviando...' : `Enviar ${TIPO_LABEL[tipo]}`}
        </button>
      </form>
    </div>
  );
}

export default Manifestacao;
